import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from 'views/App/context';
import {
  OrganizationAccreditationConfig,
  UpdateOrganizationAccreditationConfigObject,
} from '../../_types/accreditation';
import { useMutation, useQueryClient } from 'react-query';
import View from './components/View';
import Edit from './components/Edit';
import useAccreditationSolutionConfig from './_data_sources/useAccreditationSolutionConfig';
import useOrganizationAccreditationConfig from './_data_sources/useOrganizationAccreditationConfig';
import { updateOrganizationAccreditationConfig } from './_data_sources/manageOrganizationAccreditationConfig';
import { useManageSolutionsContext } from '../../context';
import InactiveState from '../../List/components/InactiveState';
import SolutionCard from '../../components/SolutionCard';

const Accreditation: React.FC = () => {
  const { selectedSolution } = useManageSolutionsContext();
  const { currentOrganizationId } = useContext(AppContext);
  const queryClient = useQueryClient();
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  // Use React Query hooks for data fetching
  const { data: accreditationSolutionConfig, isLoading: isLoadingSolutionConfig } = useAccreditationSolutionConfig();
  const { data: organizationAccreditationConfig, isLoading: isLoadingOrgConfig } = useOrganizationAccreditationConfig(
    currentOrganizationId || '',
    { enabled: !!currentOrganizationId }
  );

  const updateAccreditationSolutionMutation = useMutation(
    async (config: UpdateOrganizationAccreditationConfigObject) => {
      if (!currentOrganizationId) throw new Error('Missing organization id');
      return updateOrganizationAccreditationConfig(currentOrganizationId, config);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['accreditationSolutionConfig']);
        if (currentOrganizationId)
          queryClient.invalidateQueries(['organizationAccreditationConfig', currentOrganizationId]);
      },
    }
  );

  const [localConfig, setLocalConfig] = useState<OrganizationAccreditationConfig | null>(null);

  // Initialize local config when data loads
  useEffect(() => {
    if (organizationAccreditationConfig) {
      setLocalConfig(organizationAccreditationConfig);
    }
  }, [organizationAccreditationConfig]);

  const handleConfigChange = (updatedConfig: OrganizationAccreditationConfig) => {
    setLocalConfig(updatedConfig);
  };

  // Handle save button click
  const handleSaveClick = async () => {
    if (!localConfig) return;
    setIsSaveLoading(true);

    try {
      await updateAccreditationSolutionMutation.mutateAsync(localConfig);
    } finally {
      setIsSaveLoading(false);
    }
  };

  // Handle cancel button click - reset localConfig from cached query data and invalidate queries
  const handleCancelClick = () => {
    // Reset localConfig immediately from cached query data
    const cachedOrgConfig = currentOrganizationId
      ? queryClient.getQueryData(['organizationAccreditationConfig', currentOrganizationId])
      : null;

    if (cachedOrgConfig) {
      setLocalConfig(cachedOrgConfig as OrganizationAccreditationConfig);
    }

    // Invalidate queries to ensure we have the latest data
    queryClient.invalidateQueries(['accreditationSolutionConfig']);
    if (currentOrganizationId) {
      queryClient.invalidateQueries(['organizationAccreditationConfig', currentOrganizationId]);
    }
  };

  const isLoading = isLoadingSolutionConfig || isLoadingOrgConfig;

  if (!localConfig) {
    return (
      <SolutionCard
        solutionKey={selectedSolution!.key}
        title={selectedSolution!.name}
        view={<div>Loading...</div>}
        edit={<div>Loading...</div>}
        isLoading={true}
      />
    );
  }

  return (
    <SolutionCard
      solutionKey={selectedSolution!.key}
      title={selectedSolution!.name}
      view={<View config={localConfig} />}
      edit={
        <Edit
          config={localConfig}
          productLevels={accreditationSolutionConfig?.productLevels || []}
          onChange={handleConfigChange}
        />
      }
      onSave={handleSaveClick}
      onCancel={handleCancelClick}
      isLoading={isLoading || isSaveLoading}
    />
  );
};

export default Accreditation;
